import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import { SalesforceEvents } from '@chiroup/core/enums/Salesforce.enum';
import { ConditionReferenceCodeDisplay, Visit } from '@chiroup/core/types/PatientVisit.type';
import React, { useContext, useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MeContext } from '../../../../contexts/me.context';
import { useSandboxClinicalData } from '../../../../contexts/sandboxClinicalData.context';
import { useTrackSaopWizardEvent } from '../../../../hooks/useTrackSaopWizardEvent';
import { noteEhrStepExplored } from '../ehr/ehrExploredSteps';
import SandboxProgressBar from './components/SandboxProgressBar';
import SaopInterimModal from './components/SaopInterimModal';
import { buildSandboxVisit, SANDBOX_COMPLAINT_ID, SANDBOX_PATIENT } from './fixtures/anitaBack';
import { ConditionListRow } from './fixtures/sandboxConditions';
import { seedUnifiedObjectiveFromCondition } from './sandboxObjectiveSeeding';
import {
  initialSandboxWalkthroughState,
  SandboxStep,
  sandboxWalkthroughReducer,
} from './sandboxWalkthrough';
import DiagnosisStep from './steps/DiagnosisStep';
import OapStep from './steps/OapStep';
import ReviewSignStep from './steps/ReviewSignStep';
import SubjectiveStep from './steps/SubjectiveStep';
import { useSandboxWarmup } from './useSandboxWarmup';

/**
 * Interactive SOAP Sandbox (TTFV Story 5) — a guided, prospect-facing run
 * through a controlled new-patient encounter:
 * Review Subjective → Select Diagnosis → Finalize OAP → Review & Sign.
 *
 * Entirely self-contained: the encounter is a static fixture held in local
 * state, real EHR components are fed by props, and nothing here reads or
 * writes patient data.
 */
interface SoapSandboxProps {
  onExit?: () => void;
}

const SoapSandbox: React.FC<SoapSandboxProps> = ({ onExit }) => {
  const sandbox = useSandboxClinicalData();
  const { me } = useContext(MeContext);
  const navigate = useNavigate();
  const exit = onExit || (() => navigate('/'));
  const trackWizardEvent = useTrackSaopWizardEvent();
  const [state, dispatch] = useReducer(sandboxWalkthroughReducer, initialSandboxWalkthroughState);
  const [visitForm, setVisitForm] = useState<Visit>(buildSandboxVisit);

  const complaint = visitForm.encounterInfo?.complaints?.[0];
  const selectedConditionIds = (complaint?.assessment?.conditions ?? []).map(c => c.conditionId);

  // Start the OAP/note data fetches during steps 1–2 so later steps mount
  // onto warm caches instead of spinners.
  useSandboxWarmup(selectedConditionIds);

  // Every entry point mounts this route. Guard StrictMode's effect replay,
  // but allow a new start event when the user returns for another walkthrough.
  const startedRef = useRef(false);
  useEffect(() => {
    if (sandbox || !me?.ID || !me?.selectedClinic?.ID || startedRef.current) return;
    startedRef.current = true;
    trackWizardEvent(SalesforceEvents.SaopWizardStarted);
  }, [sandbox, me?.ID, me?.selectedClinic?.ID, trackWizardEvent]);

  const signedRef = useRef(false);
  useEffect(() => {
    if (sandbox || !state.signed || signedRef.current) return;
    signedRef.current = true;
    // Preserve the dashboard's durable roadmap progress separately from
    // Salesforce engagement, which records the click rather than completion.
    noteEhrStepExplored('saop-initial', me);
  }, [sandbox, state.signed, me]);

  // Mirrors the real Assessment step's add-condition behavior: the condition
  // lands on the complaint with its favorited reference codes (in saved
  // order), and its stock protocol data seeds the unified objective so the
  // "your diagnosis just did the work" beats have real suggestions.
  // Deselection removes only the assessment entry — like the EHR, seeded
  // objective suggestions stay until the provider edits them.
  const toggleCondition = (condition: ConditionListRow, selected: boolean) => {
    setVisitForm(prev => {
      const complaints = prev.encounterInfo?.complaints ?? [];
      const [first, ...rest] = complaints;
      if (!first) return prev;

      const existing = first.assessment?.conditions ?? [];
      let conditions: ConditionReferenceCodeDisplay[];
      let unifiedObjective = prev.unifiedObjective;

      if (selected) {
        const orderedReferenceCodes = [...(condition.referenceCodes || [])].sort(
          (a, b) => (a.order || Number.MAX_SAFE_INTEGER) - (b.order || Number.MAX_SAFE_INTEGER)
        );
        const favoritedReferenceCodes = orderedReferenceCodes.filter(code => code.favorite === 1);
        // The Assessment note lists each diagnosis's ICD-10 codes from here
        // (tester note #25). Real encounters carry only favorited codes; when
        // the clinic library has none favorited, fall back to the condition's
        // primary code so the sandbox note always shows one per diagnosis.
        const noteReferenceCodes = favoritedReferenceCodes.length
          ? favoritedReferenceCodes
          : orderedReferenceCodes.slice(0, 1);
        const primary = noteReferenceCodes[0];
        const name = condition.title || condition.name || condition.commonName || '';

        conditions = [
          ...existing,
          {
            conditionId: condition.ID,
            name,
            order: existing.length,
            essentialCondition: false,
            codeSet: primary?.codeSet,
            code: primary?.code || '',
            description: primary?.description || name,
            referenceCodes: noteReferenceCodes,
          },
        ];
        unifiedObjective = seedUnifiedObjectiveFromCondition(
          prev.unifiedObjective,
          {
            regions: condition.regions,
            muscles: condition.muscles,
            typicallyPositiveEvals: condition.typicallyPositiveEvals,
            typicallyNegativeEvals: condition.typicallyNegativeEvals,
          },
          first.id || SANDBOX_COMPLAINT_ID
        );
      } else {
        conditions = existing.filter(c => c.conditionId !== condition.ID);
      }

      return {
        ...prev,
        unifiedObjective,
        encounterInfo: {
          ...prev.encounterInfo,
          complaints: [{ ...first, assessment: { ...first.assessment, conditions } }, ...rest],
        },
      };
    });
  };

  const renderStep = () => {
    switch (state.step) {
      case SandboxStep.Subjective:
        return <SubjectiveStep onDone={() => dispatch({ type: 'advance' })} />;
      case SandboxStep.Diagnosis:
        return (
          <DiagnosisStep
            selectedConditionIds={selectedConditionIds}
            onToggleCondition={toggleCondition}
            onDone={() => dispatch({ type: 'advance' })}
            onBack={() => dispatch({ type: 'back' })}
          />
        );
      case SandboxStep.Oap:
        return (
          <OapStep
            oapSection={state.oapSection}
            visitForm={visitForm}
            setVisitForm={setVisitForm}
            onAdvance={() => dispatch({ type: 'advance' })}
            onBack={() => dispatch({ type: 'back' })}
            onJumpToSection={section => dispatch({ type: 'goToOapSection', section })}
          />
        );
      case SandboxStep.ReviewSign:
        return (
          <ReviewSignStep
            visitForm={visitForm}
            signed={state.signed}
            completed={state.completed}
            onSign={() => {
              if (!sandbox) trackWizardEvent(SalesforceEvents.SaopWizardSignNoteClicked);
              dispatch({ type: 'sign' });
            }}
            onBack={() => dispatch({ type: 'back' })}
            onContinue={() => dispatch({ type: 'continueAfterSign' })}
            onExit={exit}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-full bg-gray-50 dark:bg-dark-gray-900">
      <div className="mx-auto max-w-6xl px-3 sm:px-6 py-4 sm:py-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            {SANDBOX_PATIENT.photo ? (
              <img
                src={SANDBOX_PATIENT.photo}
                alt={`${SANDBOX_PATIENT.fname} ${SANDBOX_PATIENT.lname}`}
                className="h-10 w-10 sm:h-11 sm:w-11 shrink-0 rounded-full object-cover shadow-sm ring-1 ring-gray-200 dark:ring-dark-gray-600"
              />
            ) : (
              <span
                aria-hidden
                className="flex h-10 w-10 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-primary-700 text-sm font-bold text-white shadow-sm"
              >
                {SANDBOX_PATIENT.fname[0]}
                {SANDBOX_PATIENT.lname[0]}
              </span>
            )}
            <div className="min-w-0">
              <h1 className="truncate text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
                {SANDBOX_PATIENT.fname} {SANDBOX_PATIENT.lname}
              </h1>
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-medium text-primary-700 ring-1 ring-inset ring-primary-200 dark:bg-primary-900/30 dark:text-primary-300 dark:ring-primary-800">
                  Sample encounter
                </span>
                {/* Tester note #29: visual clutter on phones, so sm+ only. */}
                <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500 ring-1 ring-inset ring-gray-200 dark:bg-dark-gray-700 dark:text-gray-300 dark:ring-dark-gray-600">
                  <Icon icon={Icons.lock} className="h-3 w-3" />
                  Nothing here is saved
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={exit}
            className="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 shadow-sm transition-colors hover:border-gray-400 hover:text-gray-900 dark:border-dark-gray-600 dark:bg-dark-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
          >
            Exit walkthrough
          </button>
        </div>

        <div className="mt-4 rounded-xl border border-gray-200 bg-white px-3 py-3 sm:px-5 shadow-sm dark:border-dark-gray-600 dark:bg-dark-gray-800">
          <SandboxProgressBar
            current={state.step}
            maxReached={state.maxStepReached}
            locked={state.signed}
            onNavigate={step => dispatch({ type: 'goToStep', step })}
          />
        </div>

        <div className="mt-4 sm:mt-6">{renderStep()}</div>
      </div>

      {state.saopModalOpen && (
        <SaopInterimModal onContinue={() => dispatch({ type: 'dismissSaopModal' })} />
      )}
    </div>
  );
};

export default SoapSandbox;
